var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};

// src/iframeEvmProvider.ts
import EventEmitter from "events";

// src/types.ts
var IframeWalletMessageType = "particle-auth-core-iframe-wallet-message";

// src/utils.ts
import { Buffer as Buffer2 } from "buffer";
import sha256 from "crypto-js/sha256.js";
import { v4 as uuidv4 } from "uuid";
import stringify from "fast-json-stable-stringify";
function checkMessage(message) {
  const { id, messageType, data } = message;
  const { nonce, date, hash } = JSON.parse(Buffer2.from(id, "base64").toString());
  const hashValue = sha256(
    stringify({
      state: { nonce, date },
      messageType,
      data
    })
  ).toString().toLowerCase();
  if (hashValue === hash) {
    return {
      state: { nonce, date, hash },
      messageType,
      data
    };
  }
}
function buildMessage(data, messageType, iframeId) {
  const state = {
    nonce: uuidv4(),
    date: Date.now(),
    iframeId
  };
  const hashBody = stringify({
    state,
    messageType,
    data
  });
  const hashValue = sha256(hashBody).toString();
  const id = Buffer2.from(
    JSON.stringify({
      ...state,
      hash: hashValue
    })
  ).toString("base64");
  return {
    nonce: state.nonce,
    message: {
      id,
      messageType,
      data
    }
  };
}
function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

// src/iframeEvmProvider.ts
var _events, _promiseEvents, _iframeId, _passkeyOption, _initialize, initialize_fn, _onMessageEvent, _sendMessageEvent, sendMessageEvent_fn;
var EthereumProvider = class {
  constructor(options) {
    __privateAdd(this, _initialize);
    __privateAdd(this, _sendMessageEvent);
    __privateAdd(this, _events, new EventEmitter());
    __privateAdd(this, _promiseEvents, /* @__PURE__ */ new Map());
    __privateAdd(this, _iframeId, "");
    __privateAdd(this, _passkeyOption, void 0);
    this.isParticleNetwork = true;
    this.isPasskey = false;
    __privateAdd(this, _onMessageEvent, (event) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m;
      if (((_a = event.data) == null ? void 0 : _a.type) === IframeWalletMessageType) {
        const messageType = (_c = (_b = event.data) == null ? void 0 : _b.message) == null ? void 0 : _c.messageType;
        if (messageType === "ethereum-rpc" /* EthereumRpc */ || messageType === "get-passkey-option" /* GetPasskeyOption */) {
          const messageBody = checkMessage(event.data.message);
          if (messageBody) {
            const { state, data } = messageBody;
            const promiseEvent = __privateGet(this, _promiseEvents).get(state.nonce);
            if (!promiseEvent) {
              return;
            }
            if (!data) {
              promiseEvent.resolve();
            } else if (data.error) {
              promiseEvent.reject(data.error);
            } else {
              promiseEvent.resolve(data.result);
            }
            __privateGet(this, _promiseEvents).delete(state.nonce);
          } else {
            throw new Error(
              `message body hash not match, messageType: ${(_e = (_d = event.data) == null ? void 0 : _d.message) == null ? void 0 : _e.messageType}, method: ${(_h = (_g = (_f = event.data) == null ? void 0 : _f.message) == null ? void 0 : _g.data) == null ? void 0 : _h.method}`
            );
          }
        } else if (messageType === "ethereum-event" /* EthereumEvent */) {
          const messageBody = checkMessage(event.data.message);
          if (messageBody) {
            const { name, args } = messageBody.data;
            __privateGet(this, _events).emit(name, args);
          } else {
            throw new Error(
              `message body hash not match, messageType: ${(_j = (_i = event.data) == null ? void 0 : _i.message) == null ? void 0 : _j.messageType}, name: ${(_m = (_l = (_k = event.data) == null ? void 0 : _k.message) == null ? void 0 : _l.data) == null ? void 0 : _m.name}`
            );
          }
        }
      }
    });
    const { iframeId, supportCustomEvent } = options;
    __privateSet(this, _iframeId, iframeId);
    __privateGet(this, _events).setMaxListeners(100);
    if (typeof window !== "undefined") {
      window.addEventListener("message", __privateGet(this, _onMessageEvent));
    }
    __privateMethod(this, _initialize, initialize_fn).call(this, supportCustomEvent);
  }
  on(event, listener) {
    __privateGet(this, _events).on(event, listener);
    return this;
  }
  once(event, listener) {
    __privateGet(this, _events).once(event, listener);
    return this;
  }
  off(event, listener) {
    __privateGet(this, _events).off(event, listener);
    return this;
  }
  removeListener(event, listener) {
    __privateGet(this, _events).removeListener(event, listener);
    return this;
  }
  async enable() {
    return this.request({
      method: "eth_requestAccounts"
    });
  }
  async request(data) {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent, sendMessageEvent_fn).call(this, data, { resolve, reject });
    });
    return result;
  }
  async getPasskeyOption() {
    var _a;
    return (_a = __privateGet(this, _passkeyOption)) != null ? _a : null;
  }
};
_events = new WeakMap();
_promiseEvents = new WeakMap();
_iframeId = new WeakMap();
_passkeyOption = new WeakMap();
_initialize = new WeakSet();
initialize_fn = async function(customEventHandlerType) {
  if (customEventHandlerType === "passkey") {
    const result = await new Promise((resolve, reject) => {
      var _a;
      const messageData = buildMessage(void 0, "get-passkey-option" /* GetPasskeyOption */, __privateGet(this, _iframeId));
      __privateGet(this, _promiseEvents).set(messageData.nonce, { resolve, reject });
      if (typeof window !== "undefined") {
        (_a = window.parent) == null ? void 0 : _a.postMessage(
          {
            type: IframeWalletMessageType,
            message: messageData.message
          },
          "*"
        );
      }
    });
    if (result && result.credentialId) {
      this.isPasskey = true;
      __privateSet(this, _passkeyOption, result);
    } else {
      this.isPasskey = false;
      __privateSet(this, _passkeyOption, void 0);
    }
  }
};
_onMessageEvent = new WeakMap();
_sendMessageEvent = new WeakSet();
sendMessageEvent_fn = function(data, event) {
  const messageData = buildMessage(data, "ethereum-rpc" /* EthereumRpc */, __privateGet(this, _iframeId));
  __privateGet(this, _promiseEvents).set(messageData.nonce, event);
  if (typeof window !== "undefined") {
    window.parent.postMessage(
      {
        type: IframeWalletMessageType,
        message: messageData.message
      },
      "*"
    );
  }
};

// src/iframeSolanaWallet.ts
import EventEmitter2 from "events";
var _events2, _promiseEvents2, _iframeId2, _onMessageEvent2, _sendMessageEvent2, sendMessageEvent_fn2;
var SolanaWallet = class {
  constructor(iframeId) {
    __privateAdd(this, _sendMessageEvent2);
    __privateAdd(this, _events2, new EventEmitter2());
    __privateAdd(this, _promiseEvents2, /* @__PURE__ */ new Map());
    __privateAdd(this, _iframeId2, "");
    this.isParticleNetwork = true;
    this.name = "Particle";
    this.url = "https://particle.network";
    this.icon = "https://static.particle.network/wallet-icons/Particle.png";
    __privateAdd(this, _onMessageEvent2, (event) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o;
      if (((_a = event.data) == null ? void 0 : _a.type) === IframeWalletMessageType) {
        if (((_c = (_b = event.data) == null ? void 0 : _b.message) == null ? void 0 : _c.messageType) === "solana-rpc" /* SolanaRpc */) {
          const messageBody = checkMessage(event.data.message);
          if (messageBody) {
            const { state, data } = messageBody;
            const promiseEvent = __privateGet(this, _promiseEvents2).get(state.nonce);
            if (!promiseEvent) {
              return;
            }
            if (!data) {
              promiseEvent.resolve();
            } else if (data.error) {
              promiseEvent.reject(data.error);
            } else {
              promiseEvent.resolve(data.result);
            }
            __privateGet(this, _promiseEvents2).delete(state.nonce);
          } else {
            throw new Error(
              `message body hash not match, messageType: ${(_e = (_d = event.data) == null ? void 0 : _d.message) == null ? void 0 : _e.messageType}, method: ${(_h = (_g = (_f = event.data) == null ? void 0 : _f.message) == null ? void 0 : _g.data) == null ? void 0 : _h.method}`
            );
          }
        } else if (((_j = (_i = event.data) == null ? void 0 : _i.message) == null ? void 0 : _j.messageType) === "solana-event" /* SolanaEvent */) {
          const messageBody = checkMessage(event.data.message);
          if (messageBody) {
            const { name, args } = messageBody.data;
            __privateGet(this, _events2).emit(name, args);
          } else {
            throw new Error(
              `message body hash not match, messageType: ${(_l = (_k = event.data) == null ? void 0 : _k.message) == null ? void 0 : _l.messageType}, name: ${(_o = (_n = (_m = event.data) == null ? void 0 : _m.message) == null ? void 0 : _n.data) == null ? void 0 : _o.name}`
            );
          }
        }
      }
    });
    __privateSet(this, _iframeId2, iframeId);
    __privateGet(this, _events2).setMaxListeners(100);
    if (typeof window !== "undefined") {
      window.addEventListener("message", __privateGet(this, _onMessageEvent2));
    }
  }
  on(event, listener) {
    __privateGet(this, _events2).on(event, listener);
  }
  once(event, listener) {
    __privateGet(this, _events2).once(event, listener);
  }
  off(event, listener) {
    __privateGet(this, _events2).off(event, listener);
  }
  removeListener(event, listener) {
    __privateGet(this, _events2).removeListener(event, listener);
  }
  async getAddress() {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_requestAccounts"
      }, { resolve, reject });
    });
    return result;
  }
  async getChainId() {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_chainId"
      }, { resolve, reject });
    });
    return result;
  }
  async signTransaction(transaction) {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_signTransaction",
        params: [transaction.serialize({ requireAllSignatures: false, verifySignatures: false }).toString("base64")]
      }, { resolve, reject });
    });
    const { VersionedTransaction } = await import("@solana/web3.js");
    return VersionedTransaction.deserialize(Buffer.from(result, "base64"));
  }
  async signAllTransactions(transactions) {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_signAllTransactions",
        params: [
          transactions.map(
            (tx) => tx.serialize({ requireAllSignatures: false, verifySignatures: false }).toString("base64")
          )
        ]
      }, { resolve, reject });
    });
    const { VersionedTransaction } = await import("@solana/web3.js");
    return result.map((tx) => VersionedTransaction.deserialize(Buffer.from(tx, "base64")));
  }
  async signAndSendTransaction(transaction) {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_signAndSendTransaction",
        params: [transaction.serialize({ requireAllSignatures: false, verifySignatures: false }).toString("base64")]
      }, { resolve, reject });
    });
    return result;
  }
  async signMessage(message) {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_signMessage",
        params: [Buffer.from(message).toString("base64")]
      }, { resolve, reject });
    });
    return Buffer.from(result, "base64");
  }
  async switchChain(chainId) {
    const result = await new Promise((resolve, reject) => {
      __privateMethod(this, _sendMessageEvent2, sendMessageEvent_fn2).call(this, {
        method: "solana_switchChain",
        params: [chainId]
      }, { resolve, reject });
    });
    return result;
  }
};
_events2 = new WeakMap();
_promiseEvents2 = new WeakMap();
_iframeId2 = new WeakMap();
_onMessageEvent2 = new WeakMap();
_sendMessageEvent2 = new WeakSet();
sendMessageEvent_fn2 = function(data, event) {
  const messageData = buildMessage(data, "solana-rpc" /* SolanaRpc */, __privateGet(this, _iframeId2));
  __privateGet(this, _promiseEvents2).set(messageData.nonce, event);
  if (typeof window !== "undefined") {
    window.parent.postMessage(
      {
        type: IframeWalletMessageType,
        message: messageData.message
      },
      "*"
    );
  }
};

// src/iframeWallet.ts
var _initialized, _iframeId3, _promiseEvents3, _onMessageEvent3;
var IframeWallet = class {
  constructor() {
    __privateAdd(this, _initialized, void 0);
    __privateAdd(this, _iframeId3, "");
    __privateAdd(this, _promiseEvents3, /* @__PURE__ */ new Map());
    __privateAdd(this, _onMessageEvent3, (event) => {
      var _a, _b, _c, _d, _e;
      if (((_a = event.data) == null ? void 0 : _a.type) === IframeWalletMessageType) {
        const messageType = (_c = (_b = event.data) == null ? void 0 : _b.message) == null ? void 0 : _c.messageType;
        if (messageType === "get-user-info" /* GetUserInfo */ || messageType === "open-account-and-security" /* OpenAccountAndSecurity */) {
          const messageBody = checkMessage(event.data.message);
          if (messageBody) {
            const { state, data } = messageBody;
            const promiseEvent = __privateGet(this, _promiseEvents3).get(state.nonce);
            if (!promiseEvent) {
              return;
            }
            if (!data) {
              promiseEvent.resolve();
            } else if (data.error) {
              promiseEvent.reject(data.error);
            } else {
              promiseEvent.resolve(data.result);
            }
            __privateGet(this, _promiseEvents3).delete(state.nonce);
          } else {
            throw new Error(`message body hash not match, messageType: ${(_e = (_d = event.data) == null ? void 0 : _d.message) == null ? void 0 : _e.messageType}`);
          }
        }
      }
    });
    if (typeof window !== "undefined") {
      window.addEventListener("message", __privateGet(this, _onMessageEvent3));
    }
  }
  init(options) {
    if (typeof window !== "undefined" && !inIframe()) {
      throw new Error("iframe-wallet must be used in iframe");
    }
    if (__privateGet(this, _initialized) === JSON.stringify(options, Object.keys(options).sort())) {
      return;
    }
    const { iframeId, evm, solana } = options;
    if (!iframeId) {
      throw new Error("iframeId is required");
    }
    __privateSet(this, _iframeId3, iframeId);
    if (evm) {
      this.ethereum = new EthereumProvider(options);
    }
    if (solana) {
      this.solana = new SolanaWallet(iframeId);
    }
    __privateSet(this, _initialized, JSON.stringify(options, Object.keys(options).sort()));
  }
  async getUserInfo() {
    const result = await new Promise((resolve, reject) => {
      const messageData = buildMessage(void 0, "get-user-info" /* GetUserInfo */, __privateGet(this, _iframeId3));
      __privateGet(this, _promiseEvents3).set(messageData.nonce, { resolve, reject });
      if (typeof window !== "undefined") {
        window.parent.postMessage(
          {
            type: IframeWalletMessageType,
            message: messageData.message
          },
          "*"
        );
      }
    });
    return result;
  }
  async openAccountAndSecurity() {
    const result = await new Promise((resolve, reject) => {
      const messageData = buildMessage(void 0, "open-account-and-security" /* OpenAccountAndSecurity */, __privateGet(this, _iframeId3));
      __privateGet(this, _promiseEvents3).set(messageData.nonce, { resolve, reject });
      if (typeof window !== "undefined") {
        window.parent.postMessage(
          {
            type: IframeWalletMessageType,
            message: messageData.message
          },
          "*"
        );
      }
    });
    return result;
  }
  get version() {
    return "__buildVersion";
  }
};
_initialized = new WeakMap();
_iframeId3 = new WeakMap();
_promiseEvents3 = new WeakMap();
_onMessageEvent3 = new WeakMap();

// src/index.ts
var iframeWallet = new IframeWallet();
export {
  iframeWallet,
  inIframe
};
//# sourceMappingURL=index.mjs.map
